import * as React from "react";
import { connect } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import { push } from "connected-react-router";
import queryString from "query-string";
import classNames from "../../helpers/classNames";
import List from "../../components/List";
import { tl } from "../../components/translate";
import * as calendarFns from "../../components/Calendar/functions/calendarFunctions";
import LabStatusChip from "../Lab/LabStatusChip";
import { SearchBox } from "./index";
import { showLabComponentsBasedOnPermission } from "../Lab/LabDetails";
import useMobileScreen from "../../hooks/useMobileScreen";
import MedicalTestStatusTypes from "../../interfaces/Medical";
import "./MedicalLabList.scss";
import { getCustomerNumber } from "../Client/ClientList";
import MedicalSettingsPanel from "../ResourceCentre/ModuleSettings/MedicalSettings/MedicalSettingsPanel";
import Can from "../Policy/Can";
import ReportsPanel from "../Reports/Panel/ReportsPanel";

// @ts-expect-error - css
import styles from "./Lab.module.css";
import useGetOnGoing from "../../hooks/useGetOnGoing";
import { getMedicalTest, Type } from "../../actions/medicalTest";
import PageControl from "../../components/PageControl";
import { LabStatuses } from "../../interfaces/Lab";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { MedicalTestRecordParams } from "../../api/medicalTest";

interface Props {
  medicalTests: Array<any>;
  navigateTo: (v: string) => void;
  permissionGroup: string;
  restrictTestCreation: boolean;
}

function MedicalLabList({
  medicalTests,
  navigateTo,
  restrictTestCreation,
  permissionGroup
}: Props) {
  const isMobileScreen = useMobileScreen();
  const dispatch = useAppDispatch();
  const totalMedicalTest = useAppSelector((state) => state.medicalTest.total);
  const [searchParams, setSearchParams] = React.useState<MedicalTestRecordParams>({
    page: 0,
    pageSize: 30,
    testId: "",
    clientId: "",
    status: LabStatuses.ORDERED
  });
  const locationTab = queryString.parse(window.location.search)?.status as string;

  // local states
  const [tab, setTab] = React.useState<string>(locationTab || MedicalTestStatusTypes.ORDERED);
  const isLoading = useGetOnGoing(Type.GET_MEDICAL_TEST);

  const medicalTabs = {
    All: "all",
    [MedicalTestStatusTypes.ORDERED]: "ordered",
    [MedicalTestStatusTypes.SAMPLE_TAKEN]: "sampleTaken",
    [MedicalTestStatusTypes.INCOMPLETE_RESULT]: "incompleteResult",
    [MedicalTestStatusTypes.RESULT_READY]: "resultReady",
    [MedicalTestStatusTypes.CANCELED]: "canceled"
  };

  React.useEffect(() => {
    dispatch(getMedicalTest(searchParams));
  }, [searchParams]);

  const StatusTabs = () => (
    <Box display="flex" justifyContent="space-between" className={styles.filterRoot}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexWrap: "wrap",
          [theme.breakpoints.down("md")]: {
            overflowX: "auto",
            whiteSpace: "pre"
          }
        })}
      >
        {["All", ...Object.values(MedicalTestStatusTypes)].map((MedicalTestStatus) => (
          <Typography
            data-testmation="medicalStatus"
            key={MedicalTestStatus}
            className={classNames(styles.filter, {
              [styles.active]: tab === MedicalTestStatus
            })}
            onClick={() => {
              setTab(MedicalTestStatus);
              setSearchParams({
                status: MedicalTestStatus,
                page: 0,
                clientId: "",
                testId: "",
                pageSize: 30
              });
              navigateTo(`/medical?status=${MedicalTestStatus}`);
            }}
            component="span"
            style={{ marginRight: "16px" }}
          >
            {tl(`lab.${medicalTabs[MedicalTestStatus]}`)}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box className="medicalLabList">
      <Box
        sx={{
          display: "flex",
          px: "20px",
          mt: 2,
          mb: 2,
          gap: 1,
          alignItems: "center"
        }}
      >
        <Box flex={1}>
          <StatusTabs />
        </Box>
        <SearchBox onSearch={setSearchParams} searchParams={searchParams} />
        {!restrictTestCreation && showLabComponentsBasedOnPermission(permissionGroup) && (
          <Can policyAccessKey="medical:createMedical">
            <Button
              onClick={() => navigateTo("/medical/create")}
              variant="contained"
              data-testmation="createMedicalReport"
            >
              {isMobileScreen ? "Create" : tl("medical.createMedicalReport")}
            </Button>
          </Can>
        )}
        <Can policyAccessKey="medical:changeSettings">
          <MedicalSettingsPanel />
        </Can>
        <ReportsPanel module="medical" />
      </Box>
      <List
        testmationLabel="createMedicalReport"
        automation="LabList"
        isResponsive
        isLoading={isLoading}
        data={medicalTests}
        rowHeight={50}
        columns={[
          {
            key: "testId",
            label: tl("lab.testId"),
            cellRenderer: (row) => <Typography component="div">{row.id}</Typography>
          },
          {
            key: "client",
            label: tl("lab.client"),
            cellRenderer: (row) => (
              <Box>
                <Typography component="div">
                  {`${row.client?.firstName.toUpperCase()} ${row.client?.lastName.toUpperCase()}`}
                </Typography>
                <Typography>{getCustomerNumber(row.customer?.customerNumber)}</Typography>
              </Box>
            )
          },
          {
            key: "labTest",
            hideInNarrowView: true,
            label: tl("lab.labTest"),
            cellRenderer: (row) => (
              <Typography component="div">{row.results.data[0].name}</Typography>
            )
          },
          {
            key: "orderOn",
            label: tl("lab.orderedOn"),
            cellRenderer: (row) => (
              <Typography component="div">{calendarFns.bsFullDate(row.created_at)}</Typography>
            )
          },
          {
            key: "collected",
            hideInNarrowView: true,
            label: tl("lab.collected"),
            cellRenderer: (row) => (
              <Typography component="div">
                {row.collectionDate && calendarFns.bsFullDate(row.collectionDate)}
              </Typography>
            )
          },
          {
            key: "status",
            hideInNarrowView: true,
            label: tl("lab.status"),
            cellRenderer: (row) => (
              <Box display="flex" alignItems="center">
                <LabStatusChip status={row.status?.toLowerCase()} />
              </Box>
            ),
            sortable: false,
            sortFunction: (a, b) => (a > b ? 1 : -1)
          }
        ]}
        activeRow={null}
        onRowClick={(item) => {
          navigateTo(`/medical/details/${item.id}`);
        }}
        withoutSearch
        hideCreateButton
      />
      <Box className="labNavigatePage">
        <PageControl
          page={searchParams.page || 0}
          pageSize={searchParams.pageSize}
          onSetPage={(page) => {
            setSearchParams((prevState) => ({ ...prevState, page }));
          }}
          maximumDataCount={totalMedicalTest}
        />
      </Box>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  medicalTests: state.medicalTest.collection,
  permissionGroup: state.userContext?.userCreds?.userGroups[0],
  restrictTestCreation:
    state.userContext?.resourceCentre.labSettings?.enableTestCreationFromBillOnly
});

const mapDispatchToProps = (dispatch) => ({
  navigateTo: (url) => {
    dispatch(push(url));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(MedicalLabList);
